import { instance } from '../api/api';
import { toApiError } from '../utils/apiError';
import { Money } from '../utils/money';
import { IProfile } from '../types/type';

export interface IAutoparkCar {
  autonegotiation: boolean;
  autonegotiationTechnikID: string | null;
  autoparkID: string;
  carImages: {
    carID: string;
    createdAt: string;
    id: string;
    photoType: string;
    photoUrl: string;
  }[];
  carType: string;
  createdAt: string;
  diagnosticCardNumber: number;
  diagnosticCardPhoto: string | null;
  diagnosticCardVerified: boolean;
  driverID: string;
  id: string;
  issueDate: string;
  mark: string;
  model: string;
  number: string;
  osagoNumber: string | null;
  osagoPhoto: string | null;
  taxiLicense: string;
  updatedAt: string;
  validityPeriodOfDC: string;
  validityPeriodOfOsago: string | null;
  rentExpiredAt: string | null;
}

export interface IAutoparkDriver {
  birthDate: string;
  licenseNumber: string;
  licenseSer: string;
  healthRestrictions?: boolean;
  id: string;
  licenseExpireDate: string;
  licenseIssueDate: string;
  sex: string;
  snils: string;
}

export interface IAutoparkEmployee {
  address: string;
  autoparkID: string;
  avatar: string;
  driverID: string;
  email: string;
  fullName: string;
  id: string;
  isActive: boolean;
  loginDate: string;
  phone: string;
  registeredAt: string;
  role: string;
  username: string;
}

export interface IAutoparkMedlist {
  bloodAlcohol: number;
  bloodPressure: string;
  bodyTemperature: number;
  checkTime: string;
  driverID: string;
  heartRate: number;
  id: string;
  medicID: string;
  num: number;
  status: string;
  symptomsPresent: boolean;
}

export interface IAutoparkTechlist {
  body: boolean;
  carID: string;
  chassis: boolean;
  checkStatus: string;
  checkTime: string;
  engine: boolean;
  id: string;
  num: number;
  odometerReading: number;
  optic: boolean;
  technicID: string;
}

export interface IAutoparkRoadlist {
  car: IAutoparkCar;
  date: string;
  driver: IAutoparkDriver;
  id: string;
  medlist: IAutoparkMedlist;
  num: number;
  status: string;
  techlist: IAutoparkTechlist;
  url: string;
}

export interface IAutopark {
  index: string;
  actualAddress: string;
  balance: Money;
  bankName: string;
  bik: string;
  cars: IAutoparkCar[];
  currentAccount: string;
  drivers: IAutoparkDriver[];
  email: string;
  employees: IAutoparkEmployee[];
  fullnameEmployee: string;
  fullnameOwner: string;
  id: string;
  inn: string;
  kpp: string;
  legalAddress: string;
  legalName: string;
  ogrn: string;
  phone: string;
  roadlists: IAutoparkRoadlist[];
  tradeName: string;
  externalID: string;
  orgType: string;
}

export interface IAutoparksResponse {
  data: IAutopark[];
  page: number;
  totalCount: number;
  totalPages: number;
}

export interface IAutoparkCarsResponse {
  data: IAutoparkCar[];
  page: number;
  totalCount: number;
  totalPages: number;
}

export interface IAutoparkDriverWithProfileAndCars {
  // null у водителя, не завершившего регистрацию (бэкенд таких в список не отдаёт, это защита)
  driver: IProfile | null;
  profile: IProfile;
  cars: IAutoparkCar[];
}

export type IRegisteredAutoparkDriver = IAutoparkDriverWithProfileAndCars & { driver: IProfile };

export function hasDriverData(item: IAutoparkDriverWithProfileAndCars): item is IRegisteredAutoparkDriver {
  return Boolean(item.driver?.id);
}

export interface IAutoparkDriversResponse {
  data: IAutoparkDriverWithProfileAndCars[];
  page: number;
  totalCount: number;
  totalPages: number;
}

export interface ICreateAutoparkRequest {
  actualAddress: string;
  bankName: string;
  bik: string;
  currentAccount: string;
  email: string;
  externalId?: string;
  fullnameEmployee: string;
  fullnameOwner: string;
  index: string;
  inn: string;
  kpp: string;
  legalAddress: string;
  legalName: string;
  ogrn: string;
  orgType: 'fns';
  phone: string;
  tradeName: string;
}

export const AutoparkService = {
  // Создание нового автопарка
  async createAutopark(data: ICreateAutoparkRequest): Promise<IAutopark> {
    try {
      const { data: response } = await instance.post<IAutopark>('/autoparks', data);
      return response;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Не удалось создать автопарк');
    }
  },

  // Редактирование автопарка
  async updateAutopark(id: string, data: ICreateAutoparkRequest): Promise<IAutopark> {
    try {
      const { data: response } = await instance.put<IAutopark>(`/autoparks/${id}`, data);
      return response;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Не удалось обновить автопарк');
    }
  },

  async getAutoparks(page: number = 1, limit: number = 10): Promise<IAutoparksResponse> {
    try {
      const { data } = await instance.get<IAutoparksResponse>('/autoparks', {
        params: { page, limit },
      });
      return data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Не удалось получить список автопарков');
    }
  },

  // Получение автопарка по ID
  async getAutoparkById(id: string): Promise<IAutopark> {
    try {
      const { data } = await instance.get<IAutopark>(`/autoparks/${id}`);
      return data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Не удалось получить данные автопарка');
    }
  },

  // Получение автопарков доступных водителю
  async getDriverAutoparks(driverId: string): Promise<IAutopark[]> {
    try {
      const { data } = await instance.get(`/autoparks/driver/${driverId}`);
      return data;
    } catch (error: any) {
      console.error('Ошибка при получении автопарков водителя:', error);
      throw new Error(error.response?.data?.message || 'Не удалось получить автопарки водителя');
    }
  },

  // Получение автопарков медика
  async getMedicAutoparks(medicId: string): Promise<IAutopark[]> {
    try {
      // Получаем профиль медика с информацией о привязанных автопарках
      const { data } = await instance.get(`/profiles/medic/${medicId}`);
      const autoparkIDs = data.autoparkIDs || [];

      if (autoparkIDs.length === 0) {
        return [];
      }

      // Получаем информацию о каждом автопарке
      const autoparkPromises = autoparkIDs.map((id: string) =>
        instance.get(`/autoparks/${id}`).then((res) => res.data),
      );

      return await Promise.all(autoparkPromises);
    } catch (error: any) {
      console.error('Ошибка при получении автопарков медика:', error);
      throw new Error(error.response?.data?.message || 'Не удалось получить автопарки медика');
    }
  },

  // Получение автопарков техника
  async getTechnicAutoparks(technicId: string): Promise<IAutopark[]> {
    try {
      // Получаем профиль техника с информацией о привязанных автопарках
      const { data } = await instance.get(`/profiles/technik/${technicId}`);
      const autoparkIDs = data.autoparkIDs || [];

      if (autoparkIDs.length === 0) {
        return [];
      }

      // Получаем информацию о каждом автопарке
      const autoparkPromises = autoparkIDs.map((id: string) =>
        instance.get(`/autoparks/${id}`).then((res) => res.data),
      );

      return await Promise.all(autoparkPromises);
    } catch (error: any) {
      console.error('Ошибка при получении автопарков техника:', error);
      throw new Error(error.response?.data?.message || 'Не удалось получить автопарки техника');
    }
  },

  // Получение всех доступных автопарков для профиля
  async getAvailableAutoparksForProfile(profileId: string, role: string): Promise<IAutopark[]> {
    try {
      // Получаем все автопарки
      const allAutoparksResponse = await this.getAutoparks(1, 100);
      const allAutoparks = allAutoparksResponse.data || [];

      // Получаем профиль с информацией о привязанных автопарках
      const { data } = await instance.get(`/profiles/${role}/${profileId}`);
      const autoparkIDs = data.autoparkIDs || [];

      // Фильтруем, чтобы исключить уже добавленные автопарки
      return allAutoparks.filter((autopark: IAutopark) => !autoparkIDs.includes(autopark.id));
    } catch (error: any) {
      console.error('Ошибка при получении доступных автопарков:', error);
      throw new Error(error.response?.data?.message || 'Не удалось получить доступные автопарки');
    }
  },

  // Привязка профиля к автопарку
  async linkProfileToAutopark(autoparkId: string, profileId: string): Promise<void> {
    try {
      await instance.post(`/autoparks/${autoparkId}/link_profile/${profileId}`);
    } catch (error: any) {
      console.error('Ошибка при привязке профиля к автопарку:', error);
      // Сохраняем код: 409 driver_not_registered — водитель не завершил регистрацию
      throw toApiError(error, 'Не удалось привязать профиль к автопарку');
    }
  },

  // Отвязка профиля от автопарка
  async unlinkProfileFromAutopark(autoparkId: string, profileId: string): Promise<void> {
    try {
      await instance.post(`/autoparks/${autoparkId}/unlink_profile/${profileId}`);
    } catch (error: any) {
      console.error('Ошибка при отвязке профиля от автопарка:', error);
      throw new Error(error.response?.data?.message || 'Не удалось отвязать профиль от автопарка');
    }
  },

  // Получение машин автопарка с пагинацией
  async getAutoparkCars(autoparkId: string, page: number = 1, limit: number = 10): Promise<IAutoparkCarsResponse> {
    try {
      const { data } = await instance.get<IAutoparkCarsResponse>(`/autoparks/${autoparkId}/cars`, {
        params: { page, limit },
      });
      return data.data === null ? { data: [], page: 1, totalCount: 0, totalPages: 1 } : data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Не удалось получить машины автопарка');
    }
  },

  // Получение водителей автопарка с пагинацией
  async getAutoparkDrivers(
    autoparkId: string,
    page: number = 1,
    limit: number = 10,
  ): Promise<IAutoparkDriversResponse> {
    try {
      const { data } = await instance.get<IAutoparkDriversResponse>(`/autoparks/${autoparkId}/drivers`, {
        params: { page, limit },
      });
      return data.data === null ? { data: [], page: 1, totalCount: 0, totalPages: 1 } : data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Не удалось получить водителей автопарка');
    }
  },

  // Удаление водителя из автопарка
  async removeDriverFromAutopark(driverId: string, autoparkId: string): Promise<void> {
    try {
      await instance.delete(`/cars/drivers/${driverId}/autoparks/${autoparkId}`);
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Не удалось удалить водителя из автопарка');
    }
  },
};
